import { isEmpty } from 'lodash';
import moment from 'moment';
import { arrangedLineitems, formatDateOnly } from '../services/common';

// Re-export Vista validation helpers from the single source of truth
export {
  isVistaFlowSatisfied,
  isVistaLineItemFlowSatisfied,
  verifySameCodesForHeaderAndLineItems,
  buildVistaLineItemFields
} from '../services/erpValidationConfig';

export const codingfieldStates = {
  serviceCode: '',
  equipmentNo: '',
  glDivision: '',
  costType: '',
  costId: '',
  jobId: '',
  workOrder: '',
  scope: '',
  phase: '',
  glAccount: '',
  division2: '',
  division3: '',
  division4: '',
  type: ''
};

export const contentFieldsStates = {
  discount: '',
  paymentTerms: ''
};

export const calculateGrossTotal = ({ headerTotal, headerTax, headerMisc, headerFreight } = {}) => {
  const toNumber = (val) => {
    const n = parseFloat(String(val ?? '0')?.replace(/[^0-9.-]+/g, ''));
    return isNaN(n) ? 0 : n;
  };
  return (
    toNumber(headerTotal) - toNumber(headerTax) - toNumber(headerMisc) - toNumber(headerFreight)
  );
};

// ── G702 pay applications: everything comes off `total` ────────────────────────
//
// `total` is the GROSS billed this period — the figure the G703 detail lines sum
// to, what the line items are matched against, and what the FSI export's header
// amount and retainage column are both computed from. Retainage is withheld FROM
// it and the ERP does the subtraction. Current Payment Due is the remainder: a
// read-only figure on the canvas, derived here so it can never disagree with the
// total the user is looking at.
//
// This mirrors `resolveG702Amounts` in the backend's dataFormat.ts, deliberately,
// down to the rounding: the retainage is rounded to the cent FIRST and the net is
// gross minus that, so the two never differ by a cent from the exported file. If
// one rule changes, the other has to follow.

// A percentage, and bounded. Absent, blank, "10%" and — the expensive one — the
// retainage DOLLAR amount off AIA line 5 ("10 % of Completed Work  $12,345",
// which the form prints on one line and OCR reads about as often as the rate) all
// resolve to null. A rate at or above 100 is not a percentage either.
export const parseRetainageRate = (raw) => {
  if (raw === null || raw === undefined) return null;
  const cleaned = String(raw)
    .replace(/[%$,\s]/g, '')
    .trim();
  if (cleaned === '') return null;
  const percent = Number(cleaned);
  if (!Number.isFinite(percent) || percent < 0 || percent >= 100) return null;
  return percent / 100;
};

// A money cell as a number, or null when there is nothing usable in it. Absent
// and zero stay distinct: a pay app with no billed amount must render an empty
// Current Payment Due, not 0.00.
export const parseHeaderMoney = (raw) => {
  if (raw === null || raw === undefined) return null;
  const cleaned = String(raw)
    .replace(/[$,\s]/g, '')
    .trim();
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

// Current Payment Due for a G702, as a 2dp string — '' when there is no total to
// derive it from. `total` falls back to the stored `current_payment_due` for a
// document extracted before `total` was captured on a pay app: that is what the
// cell has always held there, and it is what the backend seeds the total from.
export const computeG702NetDue = ({ total, retainagePercentage, fallbackTotal } = {}) => {
  const gross = parseHeaderMoney(total) ?? parseHeaderMoney(fallbackTotal);
  if (gross === null) return '';
  const rate = parseRetainageRate(retainagePercentage);
  const retainage = rate !== null && rate > 0 ? Math.round(gross * rate * 100) / 100 : 0;
  return (Math.round((gross - retainage) * 100) / 100).toFixed(2);
};

// Vista Tax Type 2 = use / self-assessed tax: the vendor does not charge tax on the
// invoice, so the header total excludes tax (header total == line-item subtotal). Tax
// Type 1 (sales tax) is added to the total as before. Match the leading integer so both
// a bare '2' and a labeled '2 - Use Tax' resolve to use tax.
export const isUseTaxType = (taxType) => parseInt(String(taxType ?? '').trim(), 10) === 2;

// Amount to place on the consolidated line-item row. For Vista with tax calculation
// enabled the row holds the pre-tax (gross) subtotal while tax/misc sit in their own
// columns: when line items exist we use their summed amount, and when there are none we
// derive that subtotal from the header (total minus tax/misc/freight) so the row plus its
// tax/misc columns still reconcile to the header total. Otherwise the row equals the
// header total (tax dropped — same as Foundation). Shared by the initial-load, consolidate
// toggle, and reset paths so all three behave identically.
export const getConsolidatedRowAmount = ({
  erpType,
  taxCalculationPermission,
  displayTaxAsColumn,
  miscPermission,
  lineItemSum = 0,
  getHeaderVal,
  headerTotalFallback,
  isUseTax = false
} = {}) => {
  if (erpType === 'viewPointVista' && taxCalculationPermission) {
    if (Number(lineItemSum) > 0) return String(Number(lineItemSum).toFixed(2));
    const gross = calculateGrossTotal({
      headerTotal: getHeaderVal?.('total') || getHeaderVal?.('current_payment_due'),
      // Use tax (type 2) is never part of the header total, so don't net it back out.
      headerTax: displayTaxAsColumn && !isUseTax ? getHeaderVal?.('tax') : 0,
      headerMisc: miscPermission ? getHeaderVal?.('miscellaneous_amount') : 0,
      headerFreight: getHeaderVal?.('freight')
    });
    // Always the gross — even when user-entered tax/misc push it negative; the row plus
    // its tax/misc columns then still reconciles to the header total (e.g. -4.90 + 0.38 +
    // 10 = 5.48). The recalc effect distributes tax/misc onto the row regardless of sign.
    return String(gross.toFixed(2));
  }
  return headerTotalFallback;
};

// Format a distributed line-item value (e.g. tax, misc) with 2–3 decimals:
// keep 2 when that's all it has, allow up to 3, but never force a trailing
// third zero (so 7.5 -> "7.50", 7.125 -> "7.125", 7 -> "7.00").
export const formatDistributedValue = (num) => {
  const n = parseFloat(num);
  if (isNaN(n)) return '';
  const capped = parseFloat(n.toFixed(3));
  const decimals = (String(capped).split('.')[1] || '').length;
  return capped.toFixed(Math.max(2, decimals));
};

// Decimal precision to distribute a header value (tax/misc) across line items:
// match the header value's own decimal places — rounded to 3 first to absorb
// float noise — clamped to a minimum of 2 and a maximum of 3. So a header tax
// of 2 decimals -> 2-decimal line items, a 3-decimal header tax -> up to 3.
export const getDistributionPrecision = (value) => {
  const n = parseFloat(String(value ?? '').replace(/[^0-9.-]+/g, ''));
  if (isNaN(n)) return 2;
  const decimals = (String(parseFloat(n.toFixed(3))).split('.')[1] || '').length;
  return Math.min(3, Math.max(2, decimals));
};

export const calculateHeaderTotalWithoutTax = (lineItemsCopy, headerTotalStr) => {
  const toNumber = (val = '') => Number(String(val)?.replace(/[^0-9.-]/g, ''));

  // Header total
  const headerTotal = toNumber(headerTotalStr);

  // Find tax row
  const taxRow = lineItemsCopy?.find(
    (row) => row?.find((item) => item?.name === 'Description')?.value?.toLowerCase() === 'tax'
  );

  const taxAmountStr = taxRow?.find((item) => item?.name === 'Amount')?.value || '0';
  const taxAmount = toNumber(taxAmountStr);

  // Sum ALL line items (including tax)
  const lineItemsTotal = lineItemsCopy?.reduce((sum, row) => {
    const amt = row?.find((item) => item?.name === 'Amount')?.value;
    return sum + toNumber(amt);
  }, 0);

  let finalTotal = headerTotal;

  // Only subtract tax if header >= line items total
  if (headerTotal >= lineItemsTotal && taxAmount > 0) {
    finalTotal = headerTotal - taxAmount;
  }

  return String(finalTotal);
};
export function getFirstValue(str) {
  const match = str?.match(/^\d+/);
  return match ? match[0] : str;
}

// ── validate-po budget-code check (Vista) ─────────────────────────────────────────────
// validate-po also verifies that the job / phase / cost type coding is still ACTIVE in the
// company budget — at the header (`codeContent`) and per line item. The response mirrors
// that split: `poBudgetMatch.header` and `poBudgetMatch.lineItems[i]`, each carrying one
// `{ value, active }` object per code. Response key → the field name the header coding and
// line-item columns are rendered under, so a verdict can be resolved straight from a field.
// Cost id is deliberately absent: it is sent with the request but the check never reports on
// it, so it can never be flagged inactive — see PO_VALIDATION_CODE_CONTENT_FIELDS.
export const PO_BUDGET_CODE_FIELDS = {
  jobId: 'userJobId',
  phase: 'userPhases',
  costType: 'userCostType'
};

// The header codes sent as `codeContent`. Cost id rides along so the BE sees the complete
// header coding, even though only the three codes above come back with an active verdict.
export const PO_VALIDATION_CODE_CONTENT_FIELDS = [
  ...Object.values(PO_BUDGET_CODE_FIELDS),
  'userCostId'
];

// Header coding sent as `codeContent`. Every code is always sent (empty string when the
// document has no value for it) so the BE sees the full header coding, not just what is set.
export const buildPoValidationCodeContent = (accountCodeArray = [], actualaccountCodeValues = []) =>
  PO_VALIDATION_CODE_CONTENT_FIELDS?.map((name) => {
    const idx = accountCodeArray?.findIndex((item) => item?.name === name);
    return { name, value: (idx >= 0 ? actualaccountCodeValues?.[idx] : '') ?? '' };
  });

// Payload key → the `obj` (column-oriented line items) key it is read from.
const PO_VALIDATION_LINE_ITEM_COLUMNS = {
  description: 'Description',
  qty: 'Quantity',
  unit_price: 'Unit_Price',
  total: 'Amount',
  jobId: 'userJobId',
  phase: 'userPhases',
  costType: 'userCostType'
};

// Line items sent with validate-po. EVERY row goes, in display order and unfiltered (unlike
// the PO match call, which drops the tax row): `poBudgetMatch.lineItems` comes back indexed
// the same way, and that index is what maps a verdict back to its row.
export const buildPoValidationLineItems = (obj = {}) => {
  const entries = Object.entries(PO_VALIDATION_LINE_ITEM_COLUMNS);
  const rowCount = entries?.reduce(
    (max, [, column]) => Math.max(max, Array.isArray(obj?.[column]) ? obj[column].length : 0),
    0
  );
  return Array.from({ length: rowCount }, (_, index) =>
    entries?.reduce((row, [key, column]) => {
      row[key] = obj?.[column]?.[index] ?? '';
      return row;
    }, {})
  );
};

// Same payload as above, built from the RAW row-oriented line items (`[[{name, value}, …], …]`)
// instead of the column-oriented `obj`. Needed on the initial document load: `obj` is derived
// from `lineItem` in a later effect, so the load-time closure still holds the previous
// document's columns (empty on a fresh mount) and would send no line items at all. Row order is
// preserved, so `poBudgetMatch.lineItems[i]` still maps back to row i once `obj` settles.
export const buildPoValidationLineItemsFromRows = (rows = []) =>
  (Array.isArray(rows) ? rows : [])?.map((row) =>
    Object.entries(PO_VALIDATION_LINE_ITEM_COLUMNS)?.reduce((item, [key, column]) => {
      item[key] = row?.find?.((field) => field?.name === column)?.value ?? '';
      return item;
    }, {})
  );

// The inactive-code verdict for one field of one `poBudgetMatch` entry (header or a single
// line item), or '' when there is nothing to flag. Only an explicit `active: false` counts —
// `skipped` means the BE could not check the budget at all, which is not a failed check, and
// a missing code object means that code isn't part of the budget check. A field outside
// PO_BUDGET_CODE_FIELDS (cost id, gl account, …) is never flagged, whatever the entry holds.
export const getPoBudgetCodeError = (entry, fieldName, label) => {
  if (!entry || entry?.skipped === true) return '';
  const responseKey = Object.keys(PO_BUDGET_CODE_FIELDS)?.find(
    (key) => PO_BUDGET_CODE_FIELDS[key] === fieldName
  );
  if (!responseKey) return '';
  const code = entry?.[responseKey];
  if (!code || code?.active !== false) return '';
  // Just the field's own label — "Inactive Cost Type", not "Inactive Cost Type code".
  return `Inactive ${label || fieldName}`;
};

// PO -> Job Mapping (company flag `poToJobMapping`): the BE moves a PO that is really a job
// number into the job field. `document.poJobMapping` is optimistic — `{status: true, message: ''}`
// for a flag-off company, a document with no PO and a PO that did resolve — so `status === false`
// means the mapping was asked to resolve a PO into a job and could not. Surfaced as a warning on
// the job field, and only while that field is still empty: once a job is picked the miss is moot.
export const getPoJobMappingWarning = (poJobMapping, isPoToJobMappingOn, jobValue) => {
  if (!isPoToJobMappingOn) return '';
  if (poJobMapping?.status !== false) return '';
  if (String(jobValue ?? '')?.trim() !== '') return '';
  return poJobMapping?.message || '';
};

// Does the document carry ANY inactive code, header or line item? An inactive code is a hard
// stop — the coding cannot post against a budget that no longer holds it — so this drives the
// approval block, using exactly the same per-code rule the field badges are drawn from.
export const hasInactivePoBudgetCodes = (poBudgetMatch) => {
  if (!poBudgetMatch) return false;
  const entries = [poBudgetMatch?.header, ...(poBudgetMatch?.lineItems || [])];
  return entries?.some((entry) =>
    Object.values(PO_BUDGET_CODE_FIELDS)?.some((fieldName) =>
      Boolean(getPoBudgetCodeError(entry, fieldName, fieldName))
    )
  );
};

const VISTA_DUE_DATE_FORMAT = 'MM/DD/YYYY';
const VISTA_INVOICE_DATE_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'YYYY-MM-DD',
  'MM-DD-YYYY',
  'M-D-YYYY'
];

export const parseVistaPaymentTerm = (termStr) => {
  if (typeof termStr !== 'string') return null;
  const raw = termStr.trim();
  if (raw === '') return null;
  const term = raw.toUpperCase();

  if (/^0+\s*-\s*0+$/.test(term)) return { type: 'onReceipt' };
  if (/\b(?:DUE\s+)?(?:UPON|ON)\s+RECEIPT\b/.test(term)) return { type: 'onReceipt' };
  if (term === 'COD') return { type: 'onReceipt' };
  if (term === 'EOM' || term === 'END OF MONTH') return { type: 'eom' };

  const netDays = term.match(/\bNET\s+(\d{1,3})\s*(?:DAYS?)?\b(?!\s*TH?)/);
  if (netDays) return { type: 'net', days: parseInt(netDays[1], 10) };

  const nShort = term.match(/^N\s*(\d{1,3})$/);
  if (nShort) return { type: 'net', days: parseInt(nShort[1], 10) };

  // Day-of-month: accept both 'TH' and 'T' (Vista sometimes stores the abbreviated form).
  const netDay = term.match(/\bNET\s+(\d{1,2})\s*TH?\b/);
  if (netDay) {
    const discountMatch = term.match(/(\d+(?:\.\d+)?)\s*%\s*(\d{1,2})\s*TH?\b/);
    const proximo = !!discountMatch;
    const result = { type: 'dayOfMonth', day: parseInt(netDay[1], 10), proximo };
    if (discountMatch) {
      result.discountPercent = parseFloat(discountMatch[1]);
      result.discountDay = parseInt(discountMatch[2], 10);
    }
    return result;
  }

  const discountDay = term.match(/^\d+\s*%\s*(\d{1,2})\s*TH?$/);
  if (discountDay) return { type: 'dayOfMonth', day: parseInt(discountDay[1], 10) };

  const dashPair = term.match(/^(\d{1,3})\s*-\s*(\d{1,3})$/);
  if (dashPair) return { type: 'net', days: parseInt(dashPair[1], 10) };

  const withinDays = term.match(/\bWITHIN\s+(\d{1,3})\s+DAYS?\b/);
  if (withinDays) return { type: 'net', days: parseInt(withinDays[1], 10) };

  const anyDays = term.match(/\b(\d{1,3})\s+DAYS?\b/);
  if (anyDays) return { type: 'net', days: parseInt(anyDays[1], 10) };

  return null;
};

const parseInvoiceMoment = (invoiceDate) => {
  if (!invoiceDate) return null;
  const m =
    invoiceDate instanceof Date
      ? moment(invoiceDate)
      : moment(String(invoiceDate), VISTA_INVOICE_DATE_FORMATS, true);
  return m && m.isValid() ? m : null;
};

export const isValidVistaDateString = (value) => parseInvoiceMoment(value) !== null;

const dayOfMonthDue = (invoice, day, proximo = false) => {
  if (day > 31) {
    return invoice
      .clone()
      .add(1, 'month')
      .date(Math.min(day - 31, invoice.clone().add(1, 'month').daysInMonth()));
  }
  if (proximo) {
    const next = invoice.clone().add(1, 'month');
    return next.date(Math.min(day, next.daysInMonth()));
  }
  const sameMonth = invoice.clone().date(Math.min(day, invoice.daysInMonth()));
  if (sameMonth.isAfter(invoice, 'day')) return sameMonth;
  const next = invoice.clone().add(1, 'month');
  return next.date(Math.min(day, next.daysInMonth()));
};

export const calculateVistaDueDate = (invoiceDate, termStr) => {
  const invoice = parseInvoiceMoment(invoiceDate);
  if (!invoice) return '';
  const descriptor = parseVistaPaymentTerm(termStr);
  if (!descriptor) return '';

  switch (descriptor.type) {
    case 'onReceipt':
      return invoice.format(VISTA_DUE_DATE_FORMAT);
    case 'net':
      return invoice.clone().add(descriptor.days, 'days').format(VISTA_DUE_DATE_FORMAT);
    case 'eom':
      return invoice.clone().endOf('month').format(VISTA_DUE_DATE_FORMAT);
    case 'dayOfMonth':
      return dayOfMonthDue(invoice, descriptor.day, descriptor.proximo).format(
        VISTA_DUE_DATE_FORMAT
      );
    default:
      return '';
  }
};

export const calculateVistaDiscountDate = (invoiceDate, termStr) => {
  const invoice = parseInvoiceMoment(invoiceDate);
  if (!invoice) return '';
  const descriptor = parseVistaPaymentTerm(termStr);
  if (!descriptor || descriptor.discountDay == null) return '';
  return dayOfMonthDue(invoice, descriptor.discountDay, descriptor.proximo).format(
    VISTA_DUE_DATE_FORMAT
  );
};

// Transaction Date value for companies running with `transactionDateAsCurrentDate`:
// today instead of the invoice date the extraction pre-populated it with. Vista's GL
// Date is month/year only (and must be the 1st of a month), so it snaps to the 1st of
// the current month. Returns the same MM-DD-YYYY string the date fields use.
export const getCurrentTransactionDate = (erpType, today = new Date()) =>
  formatDateOnly(
    erpType === 'viewPointVista' ? new Date(today.getFullYear(), today.getMonth(), 1) : today
  );

export const getVistaDiscountPercent = (termStr) => {
  const descriptor = parseVistaPaymentTerm(termStr);
  if (!descriptor || descriptor.discountPercent == null) return '';
  return descriptor.discountPercent;
};

// Computes the Vista payment-term autofill diff: due_date, discount_date, discount.
// Returns null when nothing needs to change. Caller is responsible for applying the
// patches to actualValues / contentFields and gating on saveState.
//
// `recompute` should be true only when invoice_date or payment_term just changed.
// When false (initial load), fields are only filled if empty — existing values are
// preserved. When true (user-driven change), all three fields are kept in sync with
// the new term, including being cleared when the new term has no discount.
//
// If the term doesn't parse (calculateVistaDueDate returns ''), nothing is
// overwritten — partial typing shouldn't wipe previously valid values.
export const computeVistaPaymentTermAutofill = ({
  content,
  actualValues,
  contentFields,
  invoiceDate,
  paymentTerm,
  recompute = false
}) => {
  if (!Array.isArray(content) || !content.length) return null;
  if (!Array.isArray(actualValues) || !actualValues.length) return null;
  if (!invoiceDate || !paymentTerm) return null;

  const dueDateIndex = content.findIndex((item) => item?.name === 'due_date');
  if (dueDateIndex === -1) return null;

  const dueDate = calculateVistaDueDate(invoiceDate, paymentTerm);
  const discountDate = calculateVistaDiscountDate(invoiceDate, paymentTerm);
  const discountPercent = getVistaDiscountPercent(paymentTerm);
  // Vista's discount field holds a flat amount (no "%"). Convert the payment term's
  // discount percent to a dollar amount against the pre-tax subtotal (total − tax − misc −
  // freight). If the subtotal isn't known yet, leave discount empty rather than write "0".
  const getHeaderNum = (name) => {
    const idx = content.findIndex((c) => c?.name === name);
    return idx >= 0 ? actualValues[idx] : 0;
  };
  const discountBase = calculateGrossTotal({
    headerTotal: getHeaderNum('total') || getHeaderNum('current_payment_due'),
    headerTax: getHeaderNum('tax'),
    headerMisc: getHeaderNum('miscellaneous_amount'),
    headerFreight: getHeaderNum('freight')
  });
  const discountValue =
    discountPercent !== '' && discountBase > 0
      ? String(parseFloat(((discountPercent / 100) * discountBase).toFixed(2)))
      : '';

  const discountDateIndex = content.findIndex((item) => item?.name === 'discount_date');
  const discountIndex = content.findIndex((item) => item?.name === 'discount');
  const existingDueDate = String(actualValues[dueDateIndex] ?? '').trim();
  const existingDiscountDate = String(actualValues[discountDateIndex] ?? '').trim();
  const existingDiscountCell = String(actualValues[discountIndex] ?? '').trim();
  const existingDiscountField = String(contentFields?.discount ?? '').trim();

  // Overwriting/clearing is only safe when the term parsed successfully — otherwise
  // we'd wipe valid data while the user is mid-typing.
  const canOverwrite = recompute && !!dueDate;

  const fillDueDate =
    !!dueDate && (existingDueDate === '' || (canOverwrite && existingDueDate !== dueDate));
  const fillDiscountDate =
    discountDateIndex !== -1 &&
    ((!!discountDate && existingDiscountDate === '') ||
      (canOverwrite && existingDiscountDate !== discountDate));
  const fillDiscount =
    discountIndex !== -1 &&
    ((!!discountValue && existingDiscountCell === '') ||
      (canOverwrite && existingDiscountCell !== discountValue));
  const fillDiscountField =
    (!!discountValue && existingDiscountField === '') ||
    (canOverwrite && existingDiscountField !== discountValue);

  if (!fillDueDate && !fillDiscountDate && !fillDiscount && !fillDiscountField) return null;

  const actualValuesPatch = {};
  if (fillDueDate) actualValuesPatch[dueDateIndex] = dueDate;
  if (fillDiscountDate) actualValuesPatch[discountDateIndex] = discountDate;
  if (fillDiscount) actualValuesPatch[discountIndex] = discountValue;

  return {
    actualValuesPatch: Object.keys(actualValuesPatch).length ? actualValuesPatch : null,
    contentFieldsPatch: fillDiscountField ? { discount: discountValue } : null
  };
};

// Collect the Vista "Pay Control" options out of the synced coding data. Every synced
// vendor carries an `accountant` (its pay control) which may be blank, and gl-info also
// returns them as a flat `accountant` array — both shapes are accepted, trimmed,
// de-duplicated (case-insensitively, first spelling wins) and sorted.
export const getPayControlOptions = (glInfo = {}, extraNames = []) => {
  const flatList = Array.isArray(glInfo?.accountant) ? glInfo.accountant : [];
  const fromVendors = (glInfo?.vendor || [])?.map((vendor) => vendor?.accountant);
  const seen = new Map();
  [...flatList, ...fromVendors, ...(extraNames || [])]?.forEach((name) => {
    const trimmed = String(name ?? '')?.trim();
    if (trimmed && !seen.has(trimmed?.toLowerCase())) seen.set(trimmed?.toLowerCase(), trimmed);
  });
  return Array.from(seen.values())?.sort((a, b) => a.localeCompare(b));
};

// The pay control of ONE synced vendor, matched by ERP vendor number. Returns '' when
// the vendor isn't on the synced list or was synced without a pay control.
export const getVendorPayControl = (glInfo = {}, vendorNumber) => {
  if (!vendorNumber) return '';
  const match = (glInfo?.vendor || []).find(
    (vendor) => String(vendor?.id ?? '') === String(vendorNumber)
  );
  return String(match?.accountant ?? '').trim();
};

// Parse a currency-ish string/number into a finite number (0 on failure).
export const toNumber = (val) => {
  const n = parseFloat(String(val ?? '0').replace(/[^0-9.-]+/g, ''));
  return isNaN(n) ? 0 : n;
};

// Resolve a header discount ("2%" or a flat amount) to a dollar amount. A "%" value
// is applied to `base` (the pre-discount subtotal); a plain number is taken as-is.
// Returns a finite number (0 on empty/invalid). Note: toNumber alone can't be used on
// a "%" value — it would strip the "%" and read "2%" as $2 instead of 2% of the base.
export const resolveDiscountAmount = (rawDiscount, base = 0) => {
  const raw = String(rawDiscount ?? '').trim();
  if (raw === '') return 0;
  if (raw.includes('%')) return (toNumber(raw) / 100) * toNumber(base);
  return toNumber(raw);
};

// Distribute `headerVal` across `amounts` proportionally to each amount's share of
// `grossTotal`, rounding each to `precision` decimals (default 2) and pushing the
// rounding remainder onto the last row so the parts always sum back to `headerVal`.
// Returns numbers.
export const distributeProportionally = (amounts = [], grossTotal, headerVal, precision = 2) => {
  const values = amounts.map((amt) =>
    parseFloat(((toNumber(amt) / grossTotal) * headerVal).toFixed(precision))
  );
  const diff = parseFloat((headerVal - values.reduce((a, b) => a + b, 0)).toFixed(precision));
  if (values.length > 0) {
    values[values.length - 1] = parseFloat((values[values.length - 1] + diff).toFixed(precision));
  }
  return values;
};

// Trigger a browser download for an already-signed URL.
export const downloadFileFromUrl = (s3FileName, signedUrl) => {
  const link = document.createElement('a');
  link.href = signedUrl;
  link.setAttribute('download', s3FileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Add per-line-item `tax` / `miscellaneous_amount` / `discount` columns by proportionally
// distributing the header-level tax/misc/discount totals across the rows. The legacy "tax"
// description row is dropped when tax-as-column is active. Rows that already carry every
// applicable value (e.g. PO-fetched rows) are left untouched.
export const applyTaxAndMiscColumns = ({
  rawLineItems,
  standardContent = [],
  isTaxPerm,
  isMiscPerm,
  isDiscountPerm = false,
  isUseTax = false
}) => {
  if (!isTaxPerm && !isMiscPerm && !isDiscountPerm) return rawLineItems;

  const getHeaderVal = (name) => toNumber(standardContent.find((c) => c.name === name)?.value);
  const getHeaderRaw = (name) => standardContent.find((c) => c.name === name)?.value;
  const headerTax = isTaxPerm ? getHeaderVal('tax') : 0;
  const headerMisc = isMiscPerm ? getHeaderVal('miscellaneous_amount') : 0;
  const grossTotal = calculateGrossTotal({
    headerTotal: getHeaderVal('total') || getHeaderVal('current_payment_due'),
    // Use tax (type 2) is not in the header total, so the pre-tax subtotal (the
    // distribution denominator) must not subtract it; tax is still distributed below.
    headerTax: isUseTax ? 0 : headerTax,
    headerMisc,
    headerFreight: getHeaderVal('freight')
  });
  // Discount is a percent ("2%") or a flat amount; resolve it against the pre-tax
  // subtotal. It is NOT netted out of grossTotal (it doesn't reduce the invoice total).
  const headerDiscount = isDiscountPerm
    ? resolveDiscountAmount(getHeaderRaw('discount'), grossTotal)
    : 0;

  const isTaxDescriptionRow = (row) =>
    row?.find((f) => f.name === 'Description')?.value?.toLowerCase() === 'tax';

  // Tax-as-column drops the legacy "tax" description row entirely (tax becomes a column);
  // when it's off the tax row stays as its own line item.
  const withoutTaxRow = isTaxPerm
    ? rawLineItems?.filter((row) => !isTaxDescriptionRow(row)) ?? rawLineItems
    : rawLineItems;

  // The standalone tax line item (tax-as-column off) is never part of the distribution: it
  // must not receive a tax/misc/discount share, nor dilute the base (nor absorb the diff
  // correction, which lands on the last distributed row). When tax IS a column the row was
  // already removed above, so this only changes the tax-as-column-off case.
  const distributionRows =
    withoutTaxRow?.filter((row) => !isTaxDescriptionRow(row)) ?? withoutTaxRow;

  const distribute = (rows, headerVal) =>
    distributeProportionally(
      rows.map((row) => toNumber(row?.find((f) => f.name === 'Amount')?.value)),
      grossTotal,
      headerVal,
      getDistributionPrecision(headerVal)
    );

  const taxProportions = isTaxPerm && grossTotal > 0 ? distribute(distributionRows, headerTax) : [];
  const miscProportions =
    isMiscPerm && grossTotal > 0 ? distribute(distributionRows, headerMisc) : [];
  const discountProportions =
    isDiscountPerm && grossTotal > 0 ? distribute(distributionRows, headerDiscount) : [];

  let distRowIdx = 0;
  return (
    withoutTaxRow?.map((row) => {
      // The tax line item is skipped in the distribution index so its share stays empty;
      // every other row consumes the next proportion in order (aligned to distributionRows).
      const proportionIdx = isTaxDescriptionRow(row) ? -1 : distRowIdx++;

      const existingTax = row?.find((f) => f.name === 'tax');
      const existingMisc = row?.find((f) => f.name === 'miscellaneous_amount');
      const existingDiscount = row?.find((f) => f.name === 'discount');
      // Rows that already carry every applicable value (e.g. PO-fetched) — keep them as-is
      const taxDone = !isTaxPerm || existingTax !== undefined;
      const miscDone = !isMiscPerm || existingMisc !== undefined;
      const discountDone = !isDiscountPerm || existingDiscount !== undefined;
      if (taxDone && miscDone && discountDone) return row;

      const newRow = [...row];
      if (isTaxPerm && existingTax === undefined) {
        const val = taxProportions[proportionIdx] ?? 0;
        newRow.push({ name: 'tax', value: val > 0 ? formatDistributedValue(val) : '' });
      }
      if (isMiscPerm && existingMisc === undefined) {
        const val = miscProportions[proportionIdx] ?? 0;
        newRow.push({
          name: 'miscellaneous_amount',
          value: val > 0 ? formatDistributedValue(val) : ''
        });
      }
      if (isDiscountPerm && existingDiscount === undefined) {
        const val = discountProportions[proportionIdx] ?? 0;
        newRow.push({ name: 'discount', value: val > 0 ? formatDistributedValue(val) : '' });
      }
      return newRow;
    }) ?? withoutTaxRow
  );
};

// Vista with the `type` field on: when a PO is present, default any row whose `type` is
// empty to the matching "6-*" option (falling back to "6-PO"). Rows that already
// have a type keep it.
export const applyVistaPoTypeDefaultsToLineItems = (rawLineItems, poValue, typeOptions) => {
  const hasPo = poValue && poValue !== '-' && poValue !== 'N/A' && String(poValue).trim() !== '';
  if (!hasPo) return rawLineItems;

  const defaultPoType = typeOptions?.find((t) => /^6/.test(String(t))) || '6-PO';
  return rawLineItems?.map((row) => {
    const typeField = row?.find((f) => f?.name === 'type');
    if (typeField && String(typeField.value || '').trim() !== '') return row; // already set
    const withoutType = row?.filter((f) => f?.name !== 'type');
    return [...withoutType, { name: 'type', value: defaultPoType }];
  });
};

// Coding fields are validated separately from "real" data fields. This filters a
// per-row validation-error map down to only the non-coding errors.
const VISTA_CODING_FIELDS = [
  'userPhases',
  'userCostType',
  'scope',
  'workOrder',
  'GlAccount',
  'glAccount',
  'userCostId',
  'userJobId',
  'itemReference',
  'type'
];

export const getNonCodingLineItemErrors = (validationLineItemsErrors = {}) => {
  const nonCodingErrors = {};
  Object.entries(validationLineItemsErrors)?.forEach(([idx, errors]) => {
    const filtered = Object.entries(errors)?.filter(
      ([field]) => !VISTA_CODING_FIELDS?.includes(field)
    );
    if (filtered?.length > 0) nonCodingErrors[idx] = Object.fromEntries(filtered);
  });
  return nonCodingErrors;
};

// Same split as above, for the flat header (Account Details) error map — `{ field: message }`.
// Used to gate the Manual Approver button, which must stay reachable when the only blockers
// are coding errors that exist purely because the current user is the last approver.
export const getNonCodingHeaderErrors = (validationErrors = {}) =>
  Object.fromEntries(
    Object.entries(validationErrors)?.filter(([field]) => !VISTA_CODING_FIELDS?.includes(field))
  );

// Build the column-oriented line item object (`obj`) and the running list of column
// names from the raw `lineItem` rows. Vista pads `type` / `tax_code` / `tax_type`
// columns and preserves any non-empty `type` values already present in `prevObj`.
export const buildLineItemObj = ({
  lineItem,
  namesArray,
  erpType,
  typeFieldEnabled,
  displayTaxAsColumn,
  discountAsColumn = false,
  prevObj
}) => {
  const isVista = erpType === 'viewPointVista';
  let newNamesArray = [...namesArray];
  const arragedData = arrangedLineitems(lineItem);

  arragedData?.map((items) => {
    items?.map((element) => {
      if (isEmpty(newNamesArray)) {
        newNamesArray.push(element?.name);
      } else if (!newNamesArray?.includes(element?.name)) {
        newNamesArray.push(element?.name);
      }
    });
  });

  // Vista: ensure the type / tax_code / tax_type / discount columns are part of the shape.
  if (isVista && typeFieldEnabled && !newNamesArray?.includes('type')) newNamesArray.push('type');
  if (isVista && displayTaxAsColumn && !newNamesArray?.includes('tax_code'))
    newNamesArray.push('tax_code');
  if (isVista && displayTaxAsColumn && !newNamesArray?.includes('tax_type'))
    newNamesArray.push('tax_type');
  if (isVista && discountAsColumn && !newNamesArray?.includes('discount'))
    newNamesArray.push('discount');

  // With display-tax-as-column off, tax is not a line-item column — drop any tax
  // fields that arrived on the raw rows so the consolidated row shows no tax column.
  if (!displayTaxAsColumn) {
    newNamesArray = newNamesArray?.filter(
      (name) => name !== 'tax' && name !== 'tax_code' && name !== 'tax_type'
    );
  }

  // Discount is a line-item column only when the gate is on — drop any discount field
  // that arrived on the raw rows (e.g. legacy saved rows) so it doesn't show otherwise.
  if (!discountAsColumn) {
    newNamesArray = newNamesArray?.filter((name) => name !== 'discount');
  }

  const initialObj = newNamesArray?.reduce((accumulator, value) => {
    return { ...accumulator, [value]: [] };
  }, {});

  arragedData?.forEach((items, rowIdx) => {
    let newArray = [];
    items?.forEach((element) => {
      if (!element?.name) return;
      // Skip fields with no column (e.g. tax columns dropped when the permission is off).
      if (!Array.isArray(initialObj[element?.name])) return;
      initialObj[element?.name].push(element?.value);
      newArray.push(element?.name);
    });
    // Pad the type column — read from raw lineItem row so values set by
    // documentButtons (e.g. "6-PO" / "7-SC" for new rows) are not lost.
    if (isVista && typeFieldEnabled && !newArray.includes('type')) {
      const rawTypeValue = lineItem?.[rowIdx]?.find?.((f) => f?.name === 'type')?.value ?? '';
      initialObj['type'].push(rawTypeValue);
    }
    // Pad the tax_code column — preserve any pre-existing tax_code from the raw row.
    if (isVista && displayTaxAsColumn && !newArray.includes('tax_code')) {
      const rawTaxCode = lineItem?.[rowIdx]?.find?.((f) => f?.name === 'tax_code')?.value ?? '';
      initialObj['tax_code'].push(rawTaxCode);
    }
    // Pad the tax_type column — preserve any pre-existing tax_type from the raw row.
    if (isVista && displayTaxAsColumn && !newArray.includes('tax_type')) {
      const rawTaxType = lineItem?.[rowIdx]?.find?.((f) => f?.name === 'tax_type')?.value ?? '';
      initialObj['tax_type'].push(rawTaxType);
    }
    // Pad the discount column — preserve any pre-existing discount from the raw row.
    if (isVista && discountAsColumn && !newArray.includes('discount')) {
      const rawDiscount = lineItem?.[rowIdx]?.find?.((f) => f?.name === 'discount')?.value ?? '';
      initialObj['discount'].push(rawDiscount);
    }
  });

  // Preserve existing type values — the fetched PO line items send type as ""
  // which would wipe whatever the user had already set on each row.
  if (isVista && typeFieldEnabled && Array.isArray(initialObj['type'])) {
    initialObj['type'] = initialObj['type']?.map((v, idx) => {
      if (v && String(v).trim() !== '') return v;
      const existing = prevObj?.type?.[idx];
      return existing && String(existing).trim() !== '' ? existing : v;
    });
  }

  return { newNamesArray, initialObj };
};

// Options for the Manual Approver dropdowns (`manualApprovalSelections.jsx`).
//
// Two rules, in order:
//   1. Only signed-up company users who can actually approve — AP-side roles
//      (Accountant / Support) never appear as manual approvers.
//   2. Never anyone already on the document's `approvers` array. That array is the
//      audit trail the backend pushes an email onto on every approve / reject, so a
//      name still in the list has already acted on this document; offering them again
//      sends it back to someone who is done with it.
//
// `documentApprovers` is a flat array of emails as stored, but it is tolerant of the
// nested / object shapes the approval endpoints return so a caller can pass either.
export const getManualApproverOptions = (users = [], documentApprovers = []) => {
  const acted = new Set();
  const collect = (entry) => {
    if (!entry) return;
    if (Array.isArray(entry)) return entry.forEach(collect);
    if (typeof entry === 'string') {
      const email = entry.trim().toLowerCase();
      if (email) acted.add(email);
      return;
    }
    collect(entry?.userEmail ?? entry?.email);
  };
  collect(documentApprovers);

  return (users || [])
    ?.filter(
      (item) => item?.role !== 'Accountant' && item?.role !== 'Support' && item?.signUp === true
    )
    ?.map((item) => item?.email)
    ?.filter((email) => email && !acted.has(String(email).trim().toLowerCase()));
};
